import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { APP_STORE_CONFIG_TOKEN, APPLICATION_SUITE_TOKEN, ErrorDCR } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ApiService, AppStoreConfig, PreferenceChildData } from '@ddt-web/data-service';
import { ErrorObject, ErrorPopupService, PopupDialogAction, PopupDialogActionOptions, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { FundPreferenceService } from '@ddt-web/fund-preferences';
import { PasApiParamsService } from '@ddt-web/setup-data-workers';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import { Dictionary } from '@ngrx/entity/src/models';
import { Store } from '@ngrx/store';
import * as fromPairs from 'lodash/fromPairs';
import * as isEmpty from 'lodash/isEmpty';
import * as partition from 'lodash/partition';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, first, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { FundStatsItem } from '../models/fund-stats.interface';
import { FUND_STATUS_INTERACTIONS_STORE } from '../models/fund-status-interaction.constants';
import { FundStatusInteractionData, FundStatusInteractions, StatusInteractionType } from '../models/fund-status-interaction.interface';
import { StatusColour } from '../models/status-colour.interface';
import { FundStatusInteractionStoreService } from './fund-status-interaction-store.service';

@Injectable({
	providedIn: 'root'
})
export class FundStatusInteractionService extends FundPreferenceService {
	@InjectConfig(SharedConfigKey.MAXIMUM_STATUS_INTERACTIONS) private readonly maxFundStatusInteractions: number = 250;

	private readonly preferenceKey: string;

	protected override readonly errorDcrKeys = {
		apiFailure: ErrorDCR.FUND_STATUS_INTERACTION_API_FAILURE_ERROR,
		fundPreferencePerFundLimitExceeded: ErrorDCR.FUND_STATUS_INTERACTION_FUND_LIMIT_EXCEEDED_ERROR // should not get thrown
	};

	constructor(
		@Inject(APPLICATION_SUITE_TOKEN) private applicationSuite: string,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig,
		protected apiService: ApiService,
		protected applicationStateProxyService: ApplicationStateStoreProxyService,
		protected fundStatusInteractionStoreService: FundStatusInteractionStoreService,
		protected store: Store,
		private storeService: StoreService,
		private errorPopupService: ErrorPopupService,
		private urlService: UrlService,
		private contentService: ContentService,
		private apiParamsService: PasApiParamsService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) {
		super(apiService, fundStatusInteractionStoreService, store);
		this.preferenceKey = `${this.applicationSuite.toUpperCase()}_FUND_STATUS_INTERACTIONS`;
	}

	public getAllFundStatusInteractions(): Observable<FundStatusInteractionData[]> {
		return this.getAllFundPreference(this.preferenceKey, 'fundStatusInteractions');
	}

	public setFundStatusInteractions(statusInteractionsToSet: FundStatusInteractions, shouldIgnoreApiErrors: boolean = false): Observable<FundStatusInteractionData[]> {
		let statusInteractionsToSetEntries: [string, FundStatusInteractionData][] = Object.entries(statusInteractionsToSet);

		const fundsMissingClientId: FundStatusInteractionData[] = statusInteractionsToSetEntries
			.filter(([id]: [string, FundStatusInteractionData]) => !id)
			.map(entry => entry?.[1]);
		const fundsWithIncompleteIdentifiers: FundStatusInteractionData[] =
			Object.values(statusInteractionsToSet).filter(fundStatusInteraction => {
				return !fundStatusInteraction.cusip || !fundStatusInteraction.morningstarSecId;
			}) ?? [];

		statusInteractionsToSetEntries = statusInteractionsToSetEntries
			.filter(([id]: [string, FundStatusInteractionData]) => !!id)
			.filter(([, statusInteractionData]: [string, FundStatusInteractionData]) => !fundsMissingClientId.includes(statusInteractionData));

		return this.fundStatusInteractionStoreService.getAllFundStatusInteractions().pipe(
			take(1),
			switchMap((statusInteractionEntries: FundStatusInteractions) => {
				const [statusInteractionsToUpdate, statusInteractionsToAdd]: [FundStatusInteractions, FundStatusInteractions] = partition(
					statusInteractionsToSetEntries,
					([clientId]: [string, FundStatusInteractionData]) => !!statusInteractionEntries[clientId]
				).map(fromPairs);
				const [statusInteractionsToRemove, statusInteractionsToEdit]: [FundStatusInteractions, FundStatusInteractions] = partition(
					Object.entries(statusInteractionsToUpdate),
					([, fundStatusInteractionData]: [string, FundStatusInteractionData]) =>
						!fundStatusInteractionData.interactionType ||
						fundStatusInteractionData.interactionType === 'default'
				).map(fromPairs);

				const setPreferences$ = this.setPreferences<FundStatusInteractionData, FundStatusInteractions>(
					statusInteractionEntries,
					statusInteractionsToAdd,
					statusInteractionsToRemove,
					statusInteractionsToEdit,
					FUND_STATUS_INTERACTIONS_STORE,
					this.preferenceKey,
					this.applicationSuite,
					this.maxFundStatusInteractions,
					shouldIgnoreApiErrors
				);

				return <Observable<FundStatusInteractionData[]>>setPreferences$.pipe(
					tap(() => {
						if (fundsMissingClientId.length) {
							throw new ErrorObject({ fundStatusInteractions: fundsMissingClientId }, ErrorDCR.FUND_STATUS_INTERACTION_MISSING_ID_ERROR);
						}
						const persistedFundStatusInteractionWithIncompleteIdentifiers = fundsWithIncompleteIdentifiers
							.filter(interactionData => !Object.keys(statusInteractionsToRemove).includes(interactionData.clientId));

						if (persistedFundStatusInteractionWithIncompleteIdentifiers.length) {
							throw new ErrorObject(
								{ fundStatusInteractions: persistedFundStatusInteractionWithIncompleteIdentifiers },
								ErrorDCR.FUND_STATUS_INTERACTION_MISSING_SECONDARY_IDENTIFIERS_ERROR
							);
						}
					})
				);
			})
		);
	}

	public checkFundStatusInteractionsForUpdates(): Observable<FundStatusInteractionData[]> {
		return combineLatest([
			this.storeService.selectAll(this.storeConfig['fundStatusInteractions']),
			this.storeService.selectByIds(this.storeConfig['fundStats'], ['transient-book']),
			this.storeService.selectAll(this.storeConfig['positionDetailsPerHolding'])
		]).pipe(
			filter(([fundStatusInteractions, fundStats, positionDetailsPerHolding]) =>
				!fundStatusInteractions.isLoading && !fundStats.isLoading && !positionDetailsPerHolding.isLoading
			),
			take(1),
			switchMap(([fundStatusInteractions, fundStats]) => {
				const statusByClientId: { [clientId: string]: string } = Object.entries(fundStats.entities['transient-book']['status']).reduce(
					(clientIdByStatus, [status, stats]: [string, FundStatsItem]) => {
						stats.holdings.forEach(holding => clientIdByStatus[holding.clientId] = status);
						return clientIdByStatus;
					}, {});

				const fundsWithStatusUpdateByInteractionType = this.buildInteractionGranularChanges(fundStatusInteractions, statusByClientId);
				if (Object.keys(fundsWithStatusUpdateByInteractionType.allFunds).length) {

					const acceptedFundsWithStatusChanges: FundStatusInteractionData[] = [
						...Object.values(fundsWithStatusUpdateByInteractionType.accept.downgradedFunds).flat(),
						...Object.values(fundsWithStatusUpdateByInteractionType.accept.upgradedFunds).flat()
					];
					this.trackInteractionStatusChanges(acceptedFundsWithStatusChanges, 'accept', fundsWithStatusUpdateByInteractionType);

					const dismissedFundsWithStatusChange: FundStatusInteractionData[] = [
						...Object.values(fundsWithStatusUpdateByInteractionType.dismiss.downgradedFunds).flat(),
						...Object.values(fundsWithStatusUpdateByInteractionType.dismiss.upgradedFunds).flat()
					];
					this.trackInteractionStatusChanges(dismissedFundsWithStatusChange, 'dismiss', fundsWithStatusUpdateByInteractionType);

					if (dismissedFundsWithStatusChange.length) {
						this.applicationStateProxyService.updateIsDismissedFundsUpdated(true);

						const message = PopupDialogObject.create({
							success: false,
							title: this.contentService.get('DISMISSED_FUNDS_UPDATED_TITLE', 'The following dismissed funds have changed status:'),
							description: null,
							trackMsgPrefix: 'iq dismissed funds status change popup:',
							actions: [
								PopupDialogAction.create({
									label: this.contentService.get('DISMISSED_FUNDS_UPDATED_CONTINUE', 'Close'),
									reload: false,
									type: 'continue'
								} as PopupDialogActionOptions),
								PopupDialogAction.create({
									label: this.contentService.get('DISMISSED_FUNDS_UPDATED_REVIEW_FUNDS', 'Review Funds'),
									reload: false,
									params: this.urlService.getPortfolioIds(),
									type: 'review_dismissed_funds'
								} as PopupDialogActionOptions)
							],
							listings: Object.values(dismissedFundsWithStatusChange).map((listing: FundStatusInteractionData) =>
								({ iconType: 'none', description: '- ' + listing.ticker })
							)
						} as PopupDialogOptions);
						this.errorPopupService.openResponseMessageDialog(message);
					}

					return this.setFundStatusInteractions(fundsWithStatusUpdateByInteractionType.allFunds);
				}

				return of([]);
			})
		);
	}

	protected override translatePreferenceChildren(preferenceChildren: PreferenceChildData[]): FundStatusInteractionData[] {
		if (isEmpty(preferenceChildren)) {
			return [];
		}

		return preferenceChildren
			.filter((preferenceChild: PreferenceChildData) => !!preferenceChild && !!preferenceChild.uniqueId)
			.map((preferenceChild: PreferenceChildData) => ({
				id: preferenceChild.id,
				clientId: preferenceChild.clientId || preferenceChild.id,
				uniqueId: preferenceChild.uniqueId,
				interactionType: preferenceChild.interactionType,
				status: preferenceChild.status,
				ticker: preferenceChild.ticker,
				morningstarSecId: preferenceChild.morningstarSecId,
				cusip: preferenceChild.cusip,
				isBlkFund: preferenceChild.isBlkFund
			}));
	}

	/**
		 * Method to handle the background removal of fund status interactions for funds which are returned as an error - most likely they have been removed the universe
		 * The identifiers must be mapped to globalIds through retrieval in the associated portfolios (via flattening)
		 * The globalId is required to identify the corresponding fund status interaction object in the store for removal
		 * 
		 * @param identifiers from the partial security failure error handler. Returned in the format ticker@cusip@isin
		 * @returns The fund status interactions
		 */
	public removeInteractionsForMissingSecurities(identifiers: string[]): Observable<FundStatusInteractionData[]> {
		const portfolioIds = this.urlService.getPortfolioIds();
		return this.apiParamsService.flattenPortfolios(portfolioIds, this.storeConfig, true)
			.pipe(
				first(),
				withLatestFrom(this.fundStatusInteractionStoreService.getAllFundStatusInteractions()),
				switchMap(([flattenedPortfolios, fundStatusInteractions]: any[]) => {

					// retrieve the globalIds from errorCode funds from the associated portfolios store via flattening 
					const uniqueIdentifiers = new Set(identifiers);
					const idsToRemove: Set<string> = flattenedPortfolios.reduce((idsToRemove, flattenedPortfolio) => {
						const associatedPortfolioChildren = flattenedPortfolio.children || flattenedPortfolio.positions || [];
						associatedPortfolioChildren.reduce((idsToRemove, fund) => {
							if (uniqueIdentifiers.has(fund.identifier)) {
								idsToRemove.add(fund.globalId);
							}
							return idsToRemove;
						}, idsToRemove);

						return idsToRemove;
					}, new Set<string>());

					// identify the fund status interactions to remove based on their globalId and remove them
					const interactionsToRemove: FundStatusInteractions = Object.entries(fundStatusInteractions)
						.reduce((interactionsToRemove, [clientId, interaction]: [string, FundStatusInteractionData]) => {
							if (idsToRemove.has(clientId)) {
								const updatedInteraction = { ...interaction };
								updatedInteraction.interactionType = null;
								interactionsToRemove[clientId] = updatedInteraction;
							}
							return interactionsToRemove;
						}, {});

					return this.setFundStatusInteractions(interactionsToRemove, true);
				})
			);
	}

	private buildInteractionGranularChanges(fundStatusInteractions: Dictionary<any>, statusByClientId: { [clientId: string]: string }): StatusChangeByInteractionType {
		return Object.entries(fundStatusInteractions.entities)
			.reduce((fundsWithStatusUpdateByInteractionType, [clientId, interactionData]: [string, FundStatusInteractionData]) => {
				if (statusByClientId[clientId] && statusByClientId[clientId] !== interactionData.status) {
					const [statusChangeKey, statusChangeType] = this.buildStatusChangeKey(interactionData.status, statusByClientId[clientId]);
					const updatedInteraction: FundStatusInteractionData = { ...interactionData };
					updatedInteraction.status = statusByClientId[clientId];
					fundsWithStatusUpdateByInteractionType.allFunds[clientId] = updatedInteraction;
					fundsWithStatusUpdateByInteractionType[updatedInteraction.interactionType][statusChangeType][statusChangeKey].push(updatedInteraction);
				}
				return fundsWithStatusUpdateByInteractionType;
			}, {
				accept: {
					downgradedFunds: { greentoyellow: [], yellowtored: [], greentored: [] },
					upgradedFunds: { yellowtogreen: [], redtoyellow: [], redtogreen: [] }
				},
				dismiss: {
					downgradedFunds: { greentoyellow: [], yellowtored: [], greentored: [] },
					upgradedFunds: { yellowtogreen: [], redtoyellow: [], redtogreen: [] }
				},
				allFunds: {}
			});
	}

	private buildStatusChangeKey(interactionDataStatus: string, currentStatus: string): [string, 'upgradedFunds' | 'downgradedFunds'] {
		const isUpgraded = StatusColour.STATUS_SORT_ORDER.get(currentStatus) > StatusColour.STATUS_SORT_ORDER.get(interactionDataStatus);
		return [`${interactionDataStatus.toLowerCase()}to${currentStatus.toLowerCase()}`, isUpgraded ? 'upgradedFunds' : 'downgradedFunds'];
	}

	private trackInteractionStatusChanges(
		fundsWithStatusChanges: FundStatusInteractionData[],
		interactionType: StatusInteractionType,
		fundsWithStatusUpdateByInteractionType: StatusChangeByInteractionType
	): void {
		const acceptedFundTickers = fundsWithStatusChanges.map(interaction => interaction.ticker).join('|');
		this.tealiumAnalyticsService.trackEventTealium(new TealiumAnalyticsEvent(`iq dismissed funds status change:${interactionType}:${acceptedFundTickers}`));

		const downgradedFunds = this.buildGranularStatusChangeTrackingEventText(fundsWithStatusUpdateByInteractionType[interactionType].downgradedFunds);
		const upgradedFunds = this.buildGranularStatusChangeTrackingEventText(fundsWithStatusUpdateByInteractionType[interactionType].upgradedFunds);
		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(`iq dismissed funds status change:${interactionType}:${downgradedFunds}|${upgradedFunds}`)
		);
	}

	private buildGranularStatusChangeTrackingEventText(fundsWithStatusChange: DowngradedFundsWithStatusChange | UpgradedFundsWithStatusChange): string {
		return Object.entries(fundsWithStatusChange)
			.map(([change, interactions]: [string, FundStatusInteractionData[]]) => {
				let statusChangeAndTickers = `${change}`;
				if (interactions.length) {
					statusChangeAndTickers = statusChangeAndTickers + `|${interactions.map(interaction => interaction.ticker).join('|')}`;
				}
				return statusChangeAndTickers;
			}).join('|');
	}
}

interface StatusChangeByInteractionType {
	accept: GranularChangesByStatusChange;
	dismiss: GranularChangesByStatusChange;
	allFunds: FundStatusInteractions;
}

interface GranularChangesByStatusChange {
	downgradedFunds: DowngradedFundsWithStatusChange;
	upgradedFunds: UpgradedFundsWithStatusChange;
}

interface DowngradedFundsWithStatusChange { greentoyellow: FundStatusInteractionData[]; yellowtored: FundStatusInteractionData[]; greentored: FundStatusInteractionData[] }
interface UpgradedFundsWithStatusChange { yellowtogreen: FundStatusInteractionData[]; redtoyellow: FundStatusInteractionData[]; redtogreen: FundStatusInteractionData[] }


